import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGet, apiPost } from '../api/client';

function AdminPage({ onBack, getToken }) {
  const { t } = useTranslation();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingFor, setSavingFor] = useState(null);

  const reloadAgents = async () => {
    const listRes = await apiGet('/api/admin/agents', getToken);
    const listData = await listRes.json().catch(() => ({}));
    if (listRes.ok && Array.isArray(listData.agents)) {
      setAgents(listData.agents);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiGet('/api/admin/agents', getToken);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setError(data.error || t('admin.errorLoadAgents'));
          setAgents([]);
          return;
        }
        setAgents(Array.isArray(data.agents) ? data.agents : []);
      } catch (err) {
        if (!cancelled) setError(err.message || t('admin.errorLoadAgents'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, t]);

  const handleUpload = async (agent, kind, file) => {
    if (!file) return;
    setSavingFor(agent.user_id + ':' + kind);
    setError(null);
    try {
      const toBase64 = (f) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result;
            const base64 = typeof result === 'string' ? result.split(',').pop() : '';
            resolve(base64 || '');
          };
          reader.onerror = () => reject(reader.error || new Error('File read failed'));
          reader.readAsDataURL(f);
        });
      const base64 = await toBase64(file);
      const body = { user_id: agent.user_id };
      if (kind === 'rent') {
        body.template_base64 = base64;
        body.template_filename = file.name;
      } else if (kind === 'saleFlat') {
        body.sale_flat_template_base64 = base64;
        body.sale_flat_template_filename = file.name;
      } else if (kind === 'gdpr') {
        body.gdpr_template_base64 = base64;
      }
      const res = await apiPost('/api/admin/agents', body, getToken);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || t('admin.errorUpdateAgent'));
      }
      await reloadAgents();
    } catch (err) {
      setError(err.message || t('admin.errorUpdateAgent'));
    } finally {
      setSavingFor(null);
    }
  };

  const handlePlanAction = async (agent, action) => {
    setSavingFor(agent.user_id + ':' + action);
    setError(null);
    try {
      const res = await apiPost(
        '/api/admin/agents',
        { user_id: agent.user_id, action },
        getToken,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || t('admin.errorUpdatePlan'));
      }
      await reloadAgents();
    } catch (err) {
      setError(err.message || t('admin.errorUpdatePlan'));
    } finally {
      setSavingFor(null);
    }
  };

  const planStatusLabel = (agent) => {
    if (agent.hasPaidAccess) return t('admin.planActive');
    if (agent.entitlementStatus === 'canceled') return t('admin.planCanceled');
    return t('admin.planPending');
  };

  return (
    <div className="p-3 sm:p-4 max-w-full min-w-0">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-800">{t('admin.title')}</h2>
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          {t('admin.back')}
        </button>
      </div>
      {loading && (
        <div className="text-sm text-slate-500">{t('admin.loading')}</div>
      )}
      {error && (
        <div className="mb-3 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      {!loading && agents.length === 0 && !error && (
        <div className="text-sm text-slate-500">{t('admin.noAgents')}</div>
      )}
      {!loading && agents.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase text-slate-500">
                <th className="py-2 pr-4">{t('admin.agent')}</th>
                <th className="py-2 pr-4">{t('admin.plan')}</th>
                <th className="py-2 pr-4">{t('admin.config')}</th>
                <th className="py-2 pr-4">{t('admin.templates')}</th>
                <th className="py-2 pr-4">{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr key={agent.user_id} className="border-b border-slate-100 last:border-b-0">
                  <td className="py-2 pr-4 align-top">
                    <div className="font-medium text-slate-800 truncate max-w-xs">
                      {agent.email || t('admin.noEmail')}
                    </div>
                    {agent.full_name ? (
                      <div className="text-xs text-slate-600 truncate max-w-xs">{agent.full_name}</div>
                    ) : null}
                    <div className="text-xs text-slate-500 truncate max-w-xs">
                      {agent.user_id}
                    </div>
                  </td>
                  <td className="py-2 pr-4 align-top text-xs text-slate-600">
                    <div className="font-medium text-slate-800">{planStatusLabel(agent)}</div>
                    {!agent.hasPaidAccess ? (
                      <button
                        type="button"
                        onClick={() => handlePlanAction(agent, 'activate_plan')}
                        disabled={savingFor != null}
                        className="mt-2 inline-flex rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {t('admin.activatePlan')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handlePlanAction(agent, 'cancel_plan')}
                        disabled={savingFor != null}
                        className="mt-2 inline-flex rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        {t('admin.cancelPlan')}
                      </button>
                    )}
                  </td>
                  <td className="py-2 pr-4 align-top text-xs text-slate-600">
                    <div>
                      {agent.hasConfig
                        ? (agent.hasCuzkCredentials ? t('admin.hasCreds') : t('admin.noCreds'))
                        : t('admin.noProfileYet')}
                    </div>
                    {agent.hasConfig && (
                      <>
                        <div>
                          {agent.templateUploaded ? t('admin.rentTemplateUploaded') : t('admin.rentTemplateMissing')}
                        </div>
                        <div>
                          {agent.saleFlatTemplateUploaded
                            ? t('admin.saleFlatTemplateUploaded')
                            : t('admin.saleFlatTemplateMissing')}
                        </div>
                        <div>
                          {agent.reservationTemplateUploaded
                            ? t('admin.reservationTemplateUploaded')
                            : t('admin.reservationTemplateMissing')}
                        </div>
                        <div>
                          {agent.saleHouseTemplateUploaded
                            ? t('admin.saleHouseTemplateUploaded')
                            : t('admin.saleHouseTemplateMissing')}
                        </div>
                        <div>
                          {agent.hasGdprTemplate
                            ? t('admin.gdprTemplateUploaded')
                            : t('admin.gdprTemplateMissing')}
                        </div>
                      </>
                    )}
                  </td>
                  <td className="py-2 pr-4 align-top text-xs text-slate-600">
                    {agent.hasConfig ? (
                      <>
                        <div className="mb-1">
                          <span className="font-medium">{t('admin.uploadRent')}:</span>{' '}
                          <input
                            type="file"
                            accept=".docx"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              if (file) handleUpload(agent, 'rent', file);
                              e.target.value = '';
                            }}
                            className="mt-1 block text-xs text-slate-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700"
                            disabled={savingFor != null}
                          />
                        </div>
                        {agent.templateDownloadUrl && (
                          <a
                            href={agent.templateDownloadUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-indigo-700 hover:text-indigo-900 underline"
                          >
                            {t('admin.viewDownloadTemplate')}
                          </a>
                        )}
                        <div>
                          <span className="font-medium">{t('admin.uploadSaleFlat')}:</span>{' '}
                          <input
                            type="file"
                            accept=".docx"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              if (file) handleUpload(agent, 'saleFlat', file);
                              e.target.value = '';
                            }}
                            className="mt-1 block text-xs text-slate-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700"
                            disabled={savingFor != null}
                          />
                        </div>
                        {agent.saleFlatTemplateDownloadUrl && (
                          <a
                            href={agent.saleFlatTemplateDownloadUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-indigo-700 hover:text-indigo-900 underline"
                          >
                            {t('admin.viewDownloadTemplate')}
                          </a>
                        )}
                        <div>
                          <span className="font-medium">{t('admin.uploadGdpr')}:</span>{' '}
                          <input
                            type="file"
                            accept=".docx"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              if (file) handleUpload(agent, 'gdpr', file);
                              e.target.value = '';
                            }}
                            className="mt-1 block text-xs text-slate-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700"
                            disabled={savingFor != null}
                          />
                        </div>
                        {agent.gdprTemplateDownloadUrl && (
                          <a
                            href={agent.gdprTemplateDownloadUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-indigo-700 hover:text-indigo-900 underline"
                          >
                            {t('admin.viewDownloadTemplate')}
                          </a>
                        )}
                        {agent.reservationTemplateDownloadUrl && (
                          <div className="mt-1">
                            <span className="font-medium">{t('auth.reservationTemplateLabel')}:</span>{' '}
                            <a
                              href={agent.reservationTemplateDownloadUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-indigo-700 hover:text-indigo-900 underline"
                            >
                              {t('admin.viewDownloadTemplate')}
                            </a>
                          </div>
                        )}
                        {agent.saleHouseTemplateDownloadUrl && (
                          <div className="mt-1">
                            <span className="font-medium">{t('auth.saleHouseTemplateLabel')}:</span>{' '}
                            <a
                              href={agent.saleHouseTemplateDownloadUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-indigo-700 hover:text-indigo-900 underline"
                            >
                              {t('admin.viewDownloadTemplate')}
                            </a>
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-slate-400">{t('admin.templatesAfterSetup')}</span>
                    )}
                  </td>
                  <td className="py-2 pr-4 align-top text-xs text-slate-600">
                    <div>
                      {savingFor && savingFor.startsWith(agent.user_id + ':')
                        ? t('admin.saving')
                        : ''}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
